import React, { useState, useEffect } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { isEmpty } from "lodash";
import { TimelineMax } from "gsap";
import { useHistory } from "react-router";
import {
  Button,
  Dropdown,
  FormOptionGroup,
  Grid,
  FormField,
  Autocomplete,
} from "mesh-component-library";
import { useLocale } from "../../../contexts/LocaleContext";
import {
  FUEL_SERVICE_ID,
  GAS,
  JJ,
  TOW_SERVICE_ID,
} from "../../../app-consts/appConstants";
import { generatePath } from "../../../utilities/RedirectUtils";
import {
  sendFuelTypeToJacada,
  sendVehicleToJacada,
} from "../../../jacada/sendJacadaEventData";
import { sendJacadaRequest } from "../../../api";
import { TRANSLATION_CONSTANTS } from "../../../app-consts/translations";
import { AccidentQuestions } from "../../../components/AccidentQuestions";
import { FuelAlert } from "../../../components";
import { getTranslatedColors } from "../../../utils/translatedTextUtils";
import { AccidentQuestionResponse } from "../../../reducers/reducer-accident-questions";
import {refreshReducersToInitial} from "../../../action";
import {useDispatch} from "react-redux";
import { PartnerDetails } from "../../../types";

type AddVehicleByMakeModelProps = {
  requestMakeModel: (data: any) => void;
  resetMakeModel: () => void;
  saveRescueDetailsRetrieveResultsRequest: (data: any) => void;
  updateAccidentQuestions: (res: AccidentQuestionResponse) => void;
  updateServiceTypeOptions: (data: any) => void;
  updateVehicle: (data: any) => void;
  updateVehicleInfo: (data: any) => void;
  commonBenefits: any;
  makeModel: any;
  partnerDetails: PartnerDetails;
  serviceInfo: any;
  serviceRequestPayload: any;
  omniStreamlinedFlag?: string;
  setIsLoading: (boolean: boolean) => void;
  accidentQuestionFromRedux : AccidentQuestionResponse
};


export const AddVehicleByMakeModel = ({
  commonBenefits,
  omniStreamlinedFlag,
  makeModel,
  partnerDetails,
  requestMakeModel,
  resetMakeModel,
  saveRescueDetailsRetrieveResultsRequest,
  serviceInfo,
  serviceRequestPayload,
  setIsLoading,
  updateAccidentQuestions,
  updateServiceTypeOptions,
  updateVehicle,
  updateVehicleInfo,
  accidentQuestionFromRedux
}: AddVehicleByMakeModelProps) => {
  const dispatch = useDispatch();
  const getTranslatedText = useLocale();
  const history = useHistory();
  const isJJ = partnerDetails.partnerCode === JJ;
  let vehicle =
    serviceRequestPayload?.serviceRequest.vehicle ||
    serviceRequestPayload?.serviceRequest.vehicles;
  const [vehicleDisabled, setVehicleDisabled] = useState(!vehicle?.make);
  const [yearHasError, setYearHasError] = useState(false);
  const [reRender, setReRender] = useState(true);
  const [vehicleValue, setVehicleValue] = useState(
    vehicle?.make && vehicle?.model ? `${vehicle?.make} ${vehicle?.model}` : ""
  );
  const [year, setYear] = useState(vehicle?.year || "");
  const [make, setMake] = useState(vehicle?.make || "");
  const [model, setModel] = useState(vehicle?.model || "");
  const [vehicleColor, setVehicleColor] = useState(vehicle?.color || "");
  const [fuelType, setFuelType] = useState(
    serviceRequestPayload?.serviceRequest.serviceTypeOptions?.gas || ""
  );
  const [accidentQuestionResponse, setAccidentQuestionResponse] = useState<
    "Yes" | "No" | undefined
  >(undefined);


  const [initialVehicleState, setInitialVehicleState] = useState({
    make: "",
    model: "",
    year: "",
    color: "",
  });

  useEffect(() => {
    window.dispatchEvent(new Event("locationchange"));
    if (year) {
      setModelList(year);
    }
    setInitialVehicleState({
      make: vehicle?.make || "",
      model: vehicle?.model || "",
      year: vehicle?.year || "",
      color: vehicle?.color || "",
    });

    if(accidentQuestionResponse != accidentQuestionFromRedux){
      setAccidentQuestionResponse(accidentQuestionFromRedux)
    }
  }, []);

  useEffect(() => {
    if (makeModel.payload?.length && reRender) {
      setVehicleDisabled(false);
      setReRender(false);
      setIsLoading(false);
    }
  }, [makeModel.payload, reRender]);

  const storeAccidentQuestionResponse = () => {
    updateAccidentQuestions(accidentQuestionResponse);
  };

  const selectFuelType = (event) => {
    setFuelType(event.currentTarget.value);
    sendFuelTypeToJacada(event);
  };

  const setModelList = (year) => {
    requestMakeModel({ year });
    setIsLoading(true);
  };

  const handleAccidentQuestionChange = (e) => {
    setAccidentQuestionResponse(e.target.value as "Yes" | "No");
  };

  const enableVehicle = (event) => {
    let yearCopy = event.target.value;
    setYear(yearCopy);

    const current_year = new Date().getFullYear();

    if (yearCopy.length === 4) {
      if (yearCopy > 1970 && yearCopy < current_year + 2) {
        setYearHasError(false);
        setModelList(yearCopy);
      } else {
        setYearHasError(true);
      }
    } else {
      if (makeModel.payload?.length) {
        resetMakeModel();
      }
      setVehicleDisabled(true);
      setVehicleValue("");
      setMake("");
      setModel("");
      setReRender(true);
    }
  };

  const confirmVehicleInfo = (payload) => {
    // on submit save accident question response
    if (isJJ && accidentQuestionResponse) {
      storeAccidentQuestionResponse();
    }
    const caranimationtl = new TimelineMax({ paused: true });
    caranimationtl.play(0, false);
    let eventsData = [
      {
        name: "color_name",
        value: payload.color,
      },
    ];
    eventsData.push({
      name: "equpmnt_clss_id",
      value: 1,
    });
    let jacadaRequestData = {
      externalSystemId: localStorage.getItem("conversationId"),
      events: eventsData,
    };
    sendJacadaRequest(jacadaRequestData);
    updateVehicle(payload);
    if (serviceInfo.id === FUEL_SERVICE_ID && payload.fuelType) {
      updateServiceTypeOptions({ gas: payload.fuelType });
    }
  };

  const confirmVehicleInformation = () => {
    let vehicleColors = vehicleColor ? vehicleColor.toLowerCase() : "";
    vehicleColors =
      vehicleColors.charAt(0).toUpperCase() + vehicleColors.slice(1);

    let vehicle = {
      color: vehicleColors,
      make,
      model,
      year,
      equipmentClass: "Light",
      vin: "",
    };

    if (partnerDetails.partnerCode === JJ) {
      // @ts-ignore
      vehicle.rentalorborrowedflag = "Y";
    }

    if (fuelType) {
      updateServiceTypeOptions({ gas: fuelType });
    }
    sendVehicleToJacada(vehicle);
    setReRender(false);
    resetMakeModel();
    if (commonBenefits?.benefits) {
      handleAddedVehicle(true);
    }
    datadogRum.addAction("Redirect to Add Vehicle page");
    confirmVehicleInfo(vehicle);

    // Save rescue details after confirming vehicle information
    if(omniStreamlinedFlag){
      saveRescueDetailsRetrieveResultsRequest({
        serviceType: serviceInfo.service,
        history,
        dispatcher : () => dispatch(refreshReducersToInitial()),
        addNewVehicle : true,
        navigateOnSuccess: () => history.push(generatePath("location"))
      });
    }else{
      history.push(generatePath("location"));
    }

  };

  const handleAddedVehicle = (flag) => {
    updateVehicleInfo({ addedVehicleFlag: flag });
    updateVehicleIndex(commonBenefits.vehicles.length);
  };

  const updateVehicleIndex = (index) => {
    updateVehicleInfo({ selectedVehicleIndex: index });
  };

  const setMakeModelState = (event) => {
    if (event.target.value.value) {
      let value = event.target.value.value.split("||");
      let make = value[0].replace(/&amp;/g, "&");
      let model = value[1].replace(/&amp;/g, "&");
      setMake(make);
      setModel(model);
      setVehicleValue(`${make} ${model}`);
    }
  };

  const isButtonEnabled = () => {
    // Check if accident question is needed & and answered
    if (
      isJJ &&
      serviceInfo.id === TOW_SERVICE_ID &&
      accidentQuestionResponse === undefined
    ) {
      return false;
    }
    const vehicleChanged =
    initialVehicleState.make !== make ||
    initialVehicleState.model !== model ||
    initialVehicleState.year !== year ||
    initialVehicleState.color?.toLowerCase() !== vehicleColor?.toLowerCase();
    if(!vehicleChanged)
      return false;

    if (
      year.length === 4 &&
      vehicleColor &&
      make &&
      model &&
      vehicleColor !== getTranslatedText(TRANSLATION_CONSTANTS.SELECT_ONE)
    ) {
      if (serviceInfo.id === FUEL_SERVICE_ID) {
        return !!fuelType;
      }
      return true;
    } else {
      return false;
    }
  };

  let endYear = new Date().getFullYear() + 2;
  const getYearErrorText = (getTranslatedText) =>
    `${getTranslatedText(
      TRANSLATION_CONSTANTS.PLEASE_ENTER_CAR_MODEL
    )} ${endYear}.`;
  let vehicleSuggestions = makeModel.payload?.map((vehicle) => {
    return {
      text: vehicle.text,
      value: `${vehicle.value.make}||${vehicle.value.model}`,
    };
  });

  const fuelServiceCoverages = commonBenefits?.services?.find(
    (service) => service.type === GAS
  )?.coverages;

  return (
    <>
      <Grid.Column utils={{ vrBottom: "md" }}>
        <FormField
          hasError={yearHasError}
          errorText={getYearErrorText(getTranslatedText)}
          placeholder={getTranslatedText(TRANSLATION_CONSTANTS.YYYY)}
          name="year"
          id="year"
          label={getTranslatedText(TRANSLATION_CONSTANTS.YEAR)}
          type="tel"
          maxLength={4}
          value={year}
          onChange={(event) => {
            enableVehicle(event);
          }}
          data-testid="add-vehicle-year"
        />
      </Grid.Column>
      <Grid.Column utils={{ vrBottom: "md" }}>
        <Autocomplete
          placeholder=""
          id="vehicleinfo"
          label={getTranslatedText(TRANSLATION_CONSTANTS.MAKE_MODEL)}
          value={vehicleValue?.replace("||", " ")}
          suggestions={vehicleSuggestions}
          disabled={vehicleDisabled}
          onChange={(event) => {
            setMakeModelState(event);
          }}
          required
          data-testid="add-vehicle-autocomplete"
        />
      </Grid.Column>
      <Grid.Column>
        <Dropdown
          id="color"
          name="color"
          label={getTranslatedText(TRANSLATION_CONSTANTS.COLOR)}
          value={vehicleColor.toUpperCase()}
          defaultOption={{
            text: "",
            value: getTranslatedText(TRANSLATION_CONSTANTS.SELECT_ONE),
          }}
          options={getTranslatedColors(getTranslatedText)}
          onChange={(event) => {
            setVehicleColor(event.target.value);
          }}
          required
          sort="asc"
          data-testid="add-vehicle-color"
        />
      </Grid.Column>
      {serviceInfo.id === FUEL_SERVICE_ID && (
        <React.Fragment>
          <Grid.Column util={{ vrTop: "md" }}>
            <FormOptionGroup
              label={getTranslatedText(TRANSLATION_CONSTANTS.FUEL_TYPE)}
              id="helpmechoose"
              type="radio"
              required={true}
              options={[
                {
                  id: "helpmechoose1",
                  name: "helpmechoose",
                  text: getTranslatedText(TRANSLATION_CONSTANTS.UNLEADED),
                  value: "Unleaded",
                  defaultChecked: fuelType === "Unleaded",
                  "data-testid": "add-vehicle-fuel-unleaded",
                  onChange: (event) => {
                    selectFuelType(event);
                  },
                },
                {
                  id: "helpmechoose2",
                  name: "helpmechoose",
                  text: getTranslatedText(TRANSLATION_CONSTANTS.DIESEL),
                  value: "Diesel",
                  defaultChecked: fuelType === "Diesel",
                  "data-testid": "add-vehicle-fuel-diesel",
                  onChange: (event) => {
                    selectFuelType(event);
                  },
                },
              ]}
            />
          </Grid.Column>
          {commonBenefits.experience?.fuelCostCovered &&
            !isEmpty(fuelServiceCoverages) && (
              <Grid.Column>
                <FuelAlert fuelServiceCoverages={fuelServiceCoverages} />
              </Grid.Column>
            )}
        </React.Fragment>
      )}
      {isJJ && serviceInfo.id === TOW_SERVICE_ID && (
        <Grid.Column
          utils={{
            vrBottom: "md",
            vrTop: "lg",
          }}
        >
          <AccidentQuestions onChange={handleAccidentQuestionChange} response={accidentQuestionFromRedux} />
        </Grid.Column>
      )}
      <Grid.Column
        utils={{
          vrBottom: "md",
          vrTop: "lg",
        }}
      >
        <Button
          hasUpgrade
          shape="pill"
          size="lg"
          onClick={confirmVehicleInformation}
          disabled={!isButtonEnabled()}
          utils={{
            fullWidth: true,
          }}
          id="add-vehicle-button"
          data-testid="add-vehicle-button"
        >
          {getTranslatedText(TRANSLATION_CONSTANTS.ADD)}
        </Button>
      </Grid.Column>
    </>
  );
};
